/* eslint-disable no-unused-vars */
import React from 'react';
import { Checkbox, FormControlLabel } from '@mui/material';
import Functions from '../../../Helpers/Functions';
import { useTailwindBrand } from '../../../Components/shared';
import {
    ENV_VERIFIED_TRAINER,
    getActiveAppBrandConfig,
    getActivePrestataireBrandConfig,
    getActivePrestationBrandConfig,
    isBrandActive,
} from '../../../config/environment.config';
import { useAiciOnboardingContexte } from '../../../Contexts/AiciOnboardingContexte';
import { AiciStep } from '../../../Hooks/InterfaceAiciOnboarding';
import StepShell from '../StepShell';

interface RecapRowProps {
    label: string;
    value?: React.ReactNode;
    onEdit: () => void;
    hidden?: boolean;
}

const RecapRow = ({ label, value, onEdit, hidden }: RecapRowProps) => {
    const { classes } = useTailwindBrand();
    if (hidden) return null;
    return (
        <button
            type="button"
            onClick={onEdit}
            className="flex items-start justify-between w-full gap-2 py-2 text-left border-b border-gray-100 group last:border-0"
        >
            <span className="text-sm font-medium text-gray-500 min-w-[130px]">{label}</span>
            <span className={`text-sm font-semibold text-right ${classes.primaryText} group-hover:underline`}>
                {value || <span className="italic text-gray-300">—</span>}
            </span>
        </button>
    );
};

const RecapStep = () => {
    const {
        personal, birthBank, address, isBornFrance,
        conditions, conditionsErrors, setConditions,
        goToStep, submit, submitting,
    } = useAiciOnboardingContexte();
    const { classes, brandConfig } = useTailwindBrand();

    const fullAddress = `${address.streetNumber}${address.streetLetter ? ' ' + address.streetLetter.name : ''} ${address.streetType?.name ?? ''} ${address.streetLabel}, ${address.zipCode} ${address.city}`
        .replace(/\s+/g, ' ')
        .trim();

    const checkboxLabel = (node: React.ReactNode, required?: boolean) => (
        <span className={`text-sm text-justify ${classes.fontFamily} ${classes.text}`}>
            {node}
            {required && <span className={classes.error}>*</span>}
        </span>
    );

    const cardClass = `p-4 bg-white border border-gray-200 shadow-sm rounded-xl ${classes.fontFamily}`;
    const cardTitle = `text-xs font-bold uppercase tracking-wider mb-3 ${classes.primaryText} ${classes.fontFamily}`;

    return (
        <StepShell
            title="Récapitulatif : vérifiez vos informations"
            subtitle="Cliquez sur un champ pour le modifier. Une erreur entraînera un retard dans le traitement de votre dossier."
            onPrev={() => goToStep(AiciStep.Address)}
            onNext={submit}
            nextLabel="Créer mon compte URSSAF"
            nextLoading={submitting}
            isFinal
        >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-1">
                <div className={cardClass}>
                    <h3 className={cardTitle}>Informations personnelles</h3>
                    <RecapRow label="Civilité" value={personal.gender === '1' ? 'Monsieur' : personal.gender === '2' ? 'Madame' : ''} onEdit={() => goToStep(AiciStep.PersonalInfo)} />
                    <RecapRow label="Nom d'usage" value={personal.name} onEdit={() => goToStep(AiciStep.PersonalInfo)} />
                    <RecapRow label="Nom de naissance" value={personal.birthName} onEdit={() => goToStep(AiciStep.PersonalInfo)} hidden={!personal.birthName.trim()} />
                    <RecapRow label="Prénom(s)" value={personal.firstName} onEdit={() => goToStep(AiciStep.PersonalInfo)} />
                    <RecapRow label="Élève" value={personal.studentName} onEdit={() => goToStep(AiciStep.PersonalInfo)} hidden={!personal.studentName.trim()} />
                    <RecapRow label="E-mail" value={personal.mail} onEdit={() => goToStep(AiciStep.PersonalInfo)} />
                    <RecapRow label="Téléphone" value={personal.phoneNumber} onEdit={() => goToStep(AiciStep.PersonalInfo)} />
                </div>

                <div className={cardClass}>
                    <h3 className={cardTitle}>Naissance</h3>
                    <RecapRow label="Date de naissance" value={birthBank.birthDay} onEdit={() => goToStep(AiciStep.BirthBank)} />
                    <RecapRow label="Pays de naissance" value={isBornFrance ? 'France' : birthBank.country} onEdit={() => goToStep(AiciStep.BirthBank)} />
                    <RecapRow label="CP de naissance" value={birthBank.birthZipCode} onEdit={() => goToStep(AiciStep.BirthBank)} hidden={!isBornFrance} />
                    <RecapRow label="Ville de naissance" value={birthBank.birthCity} onEdit={() => goToStep(AiciStep.BirthBank)} hidden={!isBornFrance} />
                </div>

                <div className={cardClass}>
                    <h3 className={cardTitle}>Adresse du domicile</h3>
                    <RecapRow label="Adresse" value={fullAddress} onEdit={() => goToStep(AiciStep.Address)} />
                    <RecapRow label="Complément" value={address.complement} onEdit={() => goToStep(AiciStep.Address)} hidden={!address.complement.trim()} />
                    <RecapRow label="Lieu-dit" value={address.lieuDit} onEdit={() => goToStep(AiciStep.Address)} hidden={!address.lieuDit.trim()} />
                </div>

                <div className={cardClass}>
                    <h3 className={cardTitle}>Coordonnées bancaires</h3>
                    <RecapRow label="IBAN" value={birthBank.iban ? Functions.addEspacesIBAN(birthBank.iban) : ''} onEdit={() => goToStep(AiciStep.BirthBank)} />
                    <RecapRow label="BIC" value={birthBank.bic} onEdit={() => goToStep(AiciStep.BirthBank)} />
                    <RecapRow label="Titulaire" value={birthBank.accountHolder} onEdit={() => goToStep(AiciStep.BirthBank)} />
                </div>
            </div>

            {/* ── Acceptation des conditions ── */}
            <div className="flex flex-col gap-3 mt-8">
                <h3 className={`text-base font-bold ${classes.fontFamily} ${classes.primaryText}`}>Acceptation des conditions</h3>

                <div className={`px-4 py-3 rounded-xl border ${classes.fontFamily} ${conditionsErrors.terms ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
                    <FormControlLabel
                        className="items-start gap-2 m-0"
                        checked={conditions.terms}
                        onChange={() => setConditions({ terms: !conditions.terms })}
                        control={<Checkbox className={`${classes.primaryText} ${classes.fontFamily} p-0 mr-2`} />}
                        label={checkboxLabel(
                            <a
                                href={isBrandActive(ENV_VERIFIED_TRAINER) ? 'https://verified-trainer.com/politique-de-confidentialite' : `https://${brandConfig.domain}/policy`}
                                className="underline text-inherit" target="_blank" rel="noreferrer"
                            >
                                J'accepte la politique des données de {getActiveAppBrandConfig()}
                            </a>,
                            true
                        )}
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px', textAlign: 'justify' } }}
                    />
                    {conditionsErrors.terms && <p className={`text-xs mt-1 ${classes.error} font-semibold`}>{conditionsErrors.terms}</p>}
                </div>

                <div className={`px-4 py-3 rounded-xl border ${conditionsErrors.cgv ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
                    <FormControlLabel
                        className="items-start gap-2 m-0"
                        checked={conditions.cgv}
                        onChange={() => setConditions({ cgv: !conditions.cgv })}
                        control={<Checkbox className={`${classes.primaryText} p-0 mr-2`} />}
                        label={checkboxLabel(
                            <a
                                href={isBrandActive(ENV_VERIFIED_TRAINER) ? 'https://verified-trainer.com/cgu-cgv' : `https://${brandConfig.domain}/cgu_cgv`}
                                className="underline text-inherit" target="_blank" rel="noreferrer"
                            >
                                J'accepte les CGU et CGV
                            </a>,
                            true
                        )}
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px', textAlign: 'justify' } }}
                    />
                    {conditionsErrors.cgv && <p className={`text-xs mt-1 ${classes.error} font-semibold`}>{conditionsErrors.cgv}</p>}
                </div>

                <div className={`px-4 py-3 rounded-xl border ${conditionsErrors.cgu ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
                    <FormControlLabel
                        className="items-start gap-2 m-0"
                        checked={conditions.cgu}
                        onChange={() => setConditions({ cgu: !conditions.cgu })}
                        control={<Checkbox className={`${classes.primaryText} p-0 mr-2`} />}
                        label={checkboxLabel(
                            <>J'autorise {getActiveAppBrandConfig()} à transmettre mes informations à l'URSSAF afin que mon compte soit créé sur la plateforme particulier.urssaf.fr. Ce service, gratuit et sans obligation d'achat, est mis en place par l'URSSAF et la Direction générale des Finances publiques.</>,
                            true
                        )}
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px', textAlign: 'justify' } }}
                    />
                    {conditionsErrors.cgu && <p className={`text-xs mt-1 ${classes.error} font-semibold`}>{conditionsErrors.cgu}</p>}
                </div>

                <div className="px-4 py-3 border border-gray-200 rounded-xl bg-gray-50">
                    <FormControlLabel
                        className="items-start gap-2 m-0"
                        checked={conditions.isNotify}
                        onChange={() => setConditions({ isNotify: !conditions.isNotify })}
                        control={<Checkbox className={`${classes.primaryText} p-0 mr-2`} />}
                        label={checkboxLabel(<>Je souhaite être notifié à chaque fois que mon {getActivePrestataireBrandConfig()} déclare {getActivePrestationBrandConfig('', 'un ', 'une ')} pour moi.</>)}
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px', textAlign: 'justify' } }}
                    />
                </div>

                <div className="px-4 py-3 border border-gray-200 rounded-xl bg-gray-50">
                    <FormControlLabel
                        className="items-start gap-2 m-0"
                        checked={conditions.acceptMarketingEmail}
                        onChange={() => setConditions({ acceptMarketingEmail: !conditions.acceptMarketingEmail })}
                        control={<Checkbox className={`${classes.primaryText} p-0 mr-2`} />}
                        label={checkboxLabel(<>J'accepte de recevoir des offres promotionnelles de la part de {getActiveAppBrandConfig()}.</>)}
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px', textAlign: 'justify' } }}
                    />
                </div>
            </div>
        </StepShell>
    );
};

export default RecapStep;
